<template>
  <div class="otp-container" :class="{ 'otp-container-modal': modalMode }">
    <div class="otp-content" :class="{ 'otp-content-modal': modalMode }">
      <h2 v-if="title && !modalMode" class="otp-header">{{ title }}</h2>
      <img
        v-if="!modalMode"
        src="/images/email-illustration.png"
        class="otp-illustration"
        alt=""
        aria-hidden="true"
      />
      <h1 v-if="!modalMode" class="otp-title">Confirm your email</h1>
      <p v-if="!modalMode" class="otp-description">
        Please check your email for a six-digit One-Time Password (OTP).
      </p>

      <div class="otp-input-group">
        <label class="otp-label">OTP</label>
        <div class="otp-inputs" ref="otpInputsRef">
          <input
            v-for="(digit, index) in digits"
            :key="index"
            :ref="(el) => setInputRef(el, index)"
            v-model="digits[index]"
            type="text"
            inputmode="numeric"
            maxlength="1"
            class="otp-input"
            :aria-label="'OTP Digit ' + (index + 1)"
            :class="{
              'otp-input-error': hasError,
              'otp-input-valid': isValid,
            }"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @paste="handlePaste($event)"
            @focus="handleFocus(index)"
          />
        </div>
        <p v-if="hasError" class="otp-error-message" role="alert" aria-live="assertive">{{ errorMessageText }}</p>
        <p v-else-if="isValid" class="otp-valid-message" role="status" aria-live="polite">Code Valid</p>
        <p v-else class="otp-hint-message">Please enter the valid number</p>
      </div>

      <div class="otp-actions" :class="{ 'otp-actions-modal': modalMode }">
        <template v-if="modalMode && showResend">
          <BaseButton
            variant="outline"
            class="resend-button resend-button-modal"
            :disabled="isResending || countdown > 0"
            @click="handleResend"
          >
            Resend Code
          </BaseButton>
        </template>
        <BaseButton
          variant="primary"
          class="next-button"
          :class="{ 'next-button-modal': modalMode }"
          :disabled="
            !isComplete || isVerifying || hasError || (!modalMode && !isValid)
          "
          :loading="isVerifying"
          @click="handleVerify"
        >
          {{ modalMode ? "Continue" : "Next" }}
        </BaseButton>
        <template v-if="!modalMode && showResend">
          <BaseButton
            variant="outline"
            class="resend-button"
            :disabled="isResending || countdown > 0"
            @click="handleResend"
          >
            Resend Code
          </BaseButton>
          <p v-if="resendError" class="resend-error-message" role="alert" aria-live="assertive">
            {{ resendError }}
          </p>
        </template>
      </div>

      <template v-if="showResend">
        <div
          class="resend-timer"
          :class="{ 'resend-timer-modal': modalMode }"
          v-if="countdown > 0"
        >
          <span>You can re-send the email after</span>
          <span class="countdown-text">{{ countdown }} Seconds</span>
        </div>
        <p v-else-if="!resendError && !modalMode" class="resend-ready">
          You can re-send the email now
        </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineEmits,
  defineExpose,
} from "vue";
import BaseButton from "./common/BaseButton.vue";

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  showResend: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: "",
  },
  modalMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["verify", "resend", "validate"]);

const digits = ref(["", "", "", "", "", ""]);
const inputRefs = ref([]);
const otpInputsRef = ref(null);
const hasError = ref(false);
const errorMessageText = ref("Invalid code");
const isValid = ref(false);
const isVerifying = ref(false);
const isResending = ref(false);
const resendError = ref("");
const countdown = ref(30);
let timer = null;
let endTime = null;
const hasCountdownCompleted = ref(false);

const isComplete = computed(() => {
  return digits.value.every((digit) => digit !== "");
});

const setInputRef = (el, index) => {
  if (el) {
    inputRefs.value[index] = el;
  }
};

const clearTimer = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

const updateCountdown = () => {
  if (!endTime) return;
  const remainingMs = endTime - Date.now();
  const nextSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  countdown.value = nextSeconds;
  if (remainingMs <= 0) {
    clearTimer();
    hasCountdownCompleted.value = true;
  }
};

const startCountdown = (durationSeconds = 30) => {
  endTime = Date.now() + durationSeconds * 1000;
  clearTimer();
  hasCountdownCompleted.value = false;
  updateCountdown();
  timer = setInterval(updateCountdown, 250);
};

const handleInput = (index, event) => {
  const value = event.target.value.replace(/[^0-9]/g, "");

  if (value) {
    digits.value[index] = value;
    hasError.value = false;
    isValid.value = false;
    errorMessageText.value = "Invalid code";

    if (index < 5 && value) {
      nextTick(() => {
        inputRefs.value[index + 1]?.focus();
      });
    }

    // Validate when all 6 digits are entered
    if (isComplete.value) {
      const otp = digits.value.join("");
      emit("validate", otp);
    }
  } else {
    digits.value[index] = "";
  }
};

const handleKeydown = (index, event) => {
  if (event.key === "Backspace" && !digits.value[index] && index > 0) {
    inputRefs.value[index - 1]?.focus();
  } else if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault();
    inputRefs.value[index - 1]?.focus();
  } else if (event.key === "ArrowRight" && index < 5) {
    event.preventDefault();
    inputRefs.value[index + 1]?.focus();
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData.getData("text").replace(/[^0-9]/g, "");

  if (pastedData.length > 0) {
    const digitsToFill = pastedData.slice(0, 6).split("");
    digits.value = [
      ...digitsToFill,
      ...Array(6 - digitsToFill.length).fill(""),
    ];

    hasError.value = false;
    isValid.value = false;
    errorMessageText.value = "Invalid code";

    const lastFilledIndex = Math.min(digitsToFill.length - 1, 5);
    nextTick(() => {
      if (lastFilledIndex < 5) {
        inputRefs.value[lastFilledIndex + 1]?.focus();
      } else if (isComplete.value) {
        // Validate when all 6 digits are pasted
        const otp = digits.value.join("");
        emit("validate", otp);
      }
    });
  }
};

const handleFocus = (index) => {
  inputRefs.value[index]?.select();
};

const handleVerify = () => {
  if (!isComplete.value || isVerifying.value) return;
  // In modal mode, allow verification even if not pre-validated
  if (!props.modalMode && !isValid.value) return;

  const otp = digits.value.join("");
  isVerifying.value = true;

  emit("verify", otp);
};

const handleResend = async () => {
  if (isResending.value || countdown.value > 0) return;

  isResending.value = true;
  hasError.value = false;
  isValid.value = false;
  resendError.value = "";
  digits.value = ["", "", "", "", "", ""];

  try {
    emit("resend");
    startCountdown();
  } catch (error) {
    console.error("Resend error:", error);
  } finally {
    isResending.value = false;
  }
};

const setError = (message = "Invalid code") => {
  hasError.value = true;
  isValid.value = false;
  isVerifying.value = false;
  errorMessageText.value = message;
};

const setValid = () => {
  hasError.value = false;
  isValid.value = true;
  isVerifying.value = false;
};

const setResendError = (message) => {
  resendError.value = message || "";
};

const reset = () => {
  digits.value = ["", "", "", "", "", ""];
  hasError.value = false;
  isValid.value = false;
  isVerifying.value = false;
  errorMessageText.value = "Invalid code";
  resendError.value = "";
  hasCountdownCompleted.value = false;
};

watch(
  () => props.isLoading,
  (newValue, oldValue) => {
    if (
      oldValue === true &&
      newValue === false &&
      !timer &&
      !hasCountdownCompleted.value &&
      props.showResend
    ) {
      startCountdown();
    }
  }
);

onMounted(() => {
  if (!props.isLoading && props.showResend) {
    startCountdown();
  }
});

onBeforeUnmount(() => {
  clearTimer();
});

defineExpose({
  setError,
  setValid,
  setResendError,
  reset,
  getOTP: () => digits.value.join(""),
});
</script>

<style scoped>
.otp-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.otp-container-modal {
  align-items: flex-start;
}

.otp-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
  max-width: 508px;
}

.otp-content-modal {
  align-items: flex-start;
  gap: 24px;
  max-width: 100%;
}

.otp-illustration {
  width: 209px;
  height: 150px;
  margin-bottom: 0;
}

.otp-header {
  font-family: "League Spartan";
  font-size: 32px;
  font-style: normal;
  font-weight: 500;
  line-height: 40px;
  color: var(--primary-blue);
  text-align: left;
  width: 100%;
}

.otp-title {
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 500;
  line-height: 40px;
  color: var(--primary-blue);
  margin: 0;
  text-align: center;
}

.otp-description {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #3b4648;
  margin: 0;
  text-align: center;
}

.otp-input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.otp-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.otp-inputs {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  width: 100%;
}

.otp-input {
  height: 64px;
  width: 100%;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: white;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-size: 36px;
  font-weight: 500;
  line-height: 44px;
  color: var(--primary-blue);
  padding: 4px 16px;
  outline: none;
  transition: all 0.2s;
}

.otp-input::placeholder {
  color: #98a2b3;
}

.otp-input:focus {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 2px rgba(0, 61, 106, 0.1);
}

.otp-input-error {
  border-color: #c21d1d;
}

.otp-input-valid {
  border-color: #00925e;
}

.otp-error-message {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #c21d1d;
  margin: 4px 0 0 0;
}

.otp-valid-message {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #00925e;
  margin: 4px 0 0 0;
}

.otp-hint-message {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #6c737f;
  margin: 4px 0 0 0;
}

.otp-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.otp-actions-modal {
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
}

.next-button {
  width: 100%;
  height: 56px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.024px;
  border-radius: 12px;
}

.next-button-modal {
  width: auto;
  min-width: 100px;
  height: 40px;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  border-radius: 8px;
}

.next-button:disabled {
  background-color: #dfe2e7;
  color: var(--neutral-700);
  cursor: not-allowed;
}

.resend-button {
  width: 100%;
  height: 56px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.024px;
  border-radius: 12px;
  border-color: var(--border-color);
  color: var(--neutral-700);
}

.resend-button:not(:disabled) {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
}

.resend-button-modal {
  width: auto;
  min-width: 100px;
  height: 40px;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  border-radius: 8px;
}

.resend-error-message {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #c21d1d;
  margin: 4px 0 0 0;
  text-align: center;
}

.resend-timer {
  display: flex;
  gap: 8px;
  align-items: center;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #6c737f;
}

.resend-timer-modal {
  justify-content: center;
  width: 100%;
}

.countdown-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 700;
  line-height: 28px;
  color: #00925e;
}

.resend-ready {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #6c737f;
  margin: 0;
}

@media (max-width: 768px) {
  .otp-content {
    padding: 0;
  }

  .otp-illustration {
    width: 100%;
    max-width: 209px;
  }

  .otp-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .otp-description {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .otp-input {
    height: 56px;
    font-size: var(--fs-xxl);
    padding: 4px 8px;
  }
}
</style>
